/* SpawnAnything — head detail layer ("Kopf-Feinraster").
   In the close-up LOD the head of a unit (plus everything that overlaps its box: hair, hats, muzzle, beard)
   is rasterized at a finer voxel size than the body, so faces get ~20-26 voxels across instead of ~12.
   The fine voxels live in m.fine = { vs, map, f, region } next to the normal m.map; meshAO() meshes both
   (see meshWithFine) and the outline/animation code sees ordinary vertices with the same part ids.

   Recipe compilers hand over a list of primitives (rPrim() output) in paint order. Besides shapes the list
   may contain paint operations { op: fn(layer), part } (face painter, hair textures): they run once per
   layer, in order, on the voxels that exist at that point. Primitives built on the head/jaw frame carry
   hf: true; their bounding boxes define the fine region. */
import { faceMode } from './faceMode.js';
import * as THREE from 'three';
import { faceParts, legacyFaceFilter } from './faces.js';
import { hairParts, hatParts } from './hair.js';

const KEY = (x, y, z) => (x + 512) | ((y + 512) << 10) | ((z + 512) << 20);
const DEC = k => [(k & 1023) - 512, ((k >> 10) & 1023) - 512, ((k >> 20) & 1023) - 512];

/* Fill one primitive into a voxel map (same inclusion rules as the original recipeModel loop).
   lim: optional inclusive cell box { lo, hi } to iterate inside; skip: optional (x, y, z) => bool for cells to leave out. */
export function fillPrim(map, p, vs, lim = null, skip = null) {
  const hmin = vs * 0.55;
  const h = p.h.map(v => Math.max(v, hmin));
  const e = p.inv ? [Math.hypot(...h), Math.hypot(...h), Math.hypot(...h)] : h;
  const lo = [0, 1, 2].map(a => Math.floor((p.c[a] - e[a]) / vs)), hi = [0, 1, 2].map(a => Math.floor((p.c[a] + e[a]) / vs));
  lo[1] = Math.max(lo[1], 0);
  if (lim) for (let a = 0; a < 3; a++) { lo[a] = Math.max(lo[a], lim.lo[a]); hi[a] = Math.min(hi[a], lim.hi[a]); }
  const M = p.inv;
  for (let z = lo[2]; z <= hi[2]; z++) for (let y = lo[1]; y <= hi[1]; y++) for (let x = lo[0]; x <= hi[0]; x++) {
    if (skip && skip(x, y, z)) continue;
    let qx = (x + 0.5) * vs - p.c[0], qy = (y + 0.5) * vs - p.c[1], qz = (z + 0.5) * vs - p.c[2];
    if (M) { const tx = M[0] * qx + M[4] * qy + M[8] * qz, ty = M[1] * qx + M[5] * qy + M[9] * qz, tz = M[2] * qx + M[6] * qy + M[10] * qz; qx = tx; qy = ty; qz = tz; }
    const ax = Math.abs(qx) / h[0], ay = Math.abs(qy) / h[1], az = Math.abs(qz) / h[2];
    let inside;
    if (p.shape === 'ellipsoid') inside = ax * ax + ay * ay + az * az <= 1;
    else if (p.shape === 'cylinder') inside = p.axis === 'x' ? ax <= 1 && ay * ay + az * az <= 1 : p.axis === 'z' ? az <= 1 && ax * ax + ay * ay <= 1 : ay <= 1 && ax * ax + az * az <= 1;
    else if (p.shape === 'cone') { const rf = 1 - ((qy / h[1] + 1) / 2) * 0.9; inside = ay <= 1 && ax * ax + az * az <= rf * rf; }
    else inside = ax <= 1 && ay <= 1 && az <= 1;
    if (!inside) continue;
    let c = p.color;
    if (p.stripes) { const wv = [(x + 0.5) * vs, (y + 0.5) * vs, (z + 0.5) * vs][p.stripes.axis]; if (Math.floor(wv / p.stripes.period) & 1) c = p.stripes.color; }
    else if (p.checker !== null && p.checker !== undefined && ((x + y + z) & 1)) c = p.checker;
    map.set(KEY(x, y, z), p.face ? { c, p: p.part, e: p.glow ? 1 : 0, f: 1 } : { c, p: p.part, e: p.glow ? 1 : 0 });
  }
}

/* A voxel layer as seen by paint operations. Coordinates are cells of this layer (size vs). */
export class Layer {
  constructor(map, vs, f, own) { this.map = map; this.vs = vs; this.f = f; this.own = own; this.fine = f > 1; }
  get(x, y, z) { return this.map.get(KEY(x, y, z)); }
  has(x, y, z) { return this.map.has(KEY(x, y, z)); }
  /* set only where this layer owns the cell (fine layer: inside the region, coarse layer: outside) */
  set(x, y, z, c, p, e = 0) { if (Number.isFinite(c) && this.own(x, y, z)) this.map.set(KEY(x, y, z), { c, p, e }); }
  del(x, y, z) { if (this.own(x, y, z)) this.map.delete(KEY(x, y, z)); }
}

/* Region (inclusive coarse-cell box) covering all head-frame primitives, clipped to cap ({ lo, hi } in metres). */
export function headRegion(prims, vs, cap) {
  const mn = [Infinity, Infinity, Infinity], mx = [-Infinity, -Infinity, -Infinity];
  for (const p of prims) {
    if (!p.hf) continue;
    const h = p.op ? p.h : p.h.map(v => Math.max(v, vs * 0.55));
    const e = p.inv ? [Math.hypot(...h), Math.hypot(...h), Math.hypot(...h)] : h;
    for (let a = 0; a < 3; a++) { mn[a] = Math.min(mn[a], p.c[a] - e[a]); mx[a] = Math.max(mx[a], p.c[a] + e[a]); }
  }
  if (!(mn[0] < mx[0])) return null;
  const lo = [0, 1, 2].map(a => Math.floor(Math.max(mn[a], cap.lo[a]) / vs)), hi = [0, 1, 2].map(a => Math.floor(Math.min(mx[a], cap.hi[a]) / vs));
  lo[1] = Math.max(lo[1], 0);
  return hi.every((v, a) => v >= lo[a]) ? { lo, hi } : null;
}

/* Fine factor so the head front gets ~18-40 voxels across (1 = no fine layer). */
export function fineFactor(headHalfWidth, vs) {
  const w = 2 * headHalfWidth / vs;
  // giants (coarse voxels) only refine small heads: their head region would pull in lots of body voxels
  return w >= (vs > 0.08 ? 14 : 20) ? 1 : w * 2 >= 18 || w * 3 > 34 ? 2 : 3;
}

/* Rasterize primitives (and paint ops) into model m (coarse map at vs). With fine = { f, region } the region
   cells go to m.fine instead, f times finer. Returns m. */
export function rasterize(m, prims, vs, fine = null) {
  const R = fine && fine.f > 1 && fine.region ? fine.region : null;
  // ownership: region cells that end up empty or in a head part (fine.parts) go to the fine layer; body voxels that
  // reach into the region (shoulders, collars, a chariot behind a horse head) stay coarse
  let owned = null;
  if (R) {
    const probe = new Map(), hp = fine.parts || [1];
    for (const p of prims) if (!p.op) fillPrim(probe, p, vs, R, null);
    owned = new Set();
    for (let z = R.lo[2]; z <= R.hi[2]; z++) for (let y = R.lo[1]; y <= R.hi[1]; y++) for (let x = R.lo[0]; x <= R.hi[0]; x++) {
      const k = KEY(x, y, z), v = probe.get(k);
      if (!v || hp.includes(v.p)) owned.add(k);
    }
  }
  const isFine = (x, y, z) => owned.has(KEY(x, y, z));
  const coarse = new Layer(m.map, vs, 1, R ? (x, y, z) => !isFine(x, y, z) : () => true);
  let fl = null, flim = null;
  if (R) {
    const f = fine.f;
    flim = { lo: R.lo.map(v => v * f), hi: R.hi.map(v => v * f + f - 1) };
    const fmap = new Map();
    const fOwn = (x, y, z) => isFine(Math.floor(x / f), Math.floor(y / f), Math.floor(z / f));
    fl = new Layer(fmap, vs / f, f, (x, y, z) => x >= flim.lo[0] && x <= flim.hi[0] && y >= flim.lo[1] && y <= flim.hi[1] && z >= flim.lo[2] && z <= flim.hi[2] && fOwn(x, y, z));
    fl.skip = (x, y, z) => !fOwn(x, y, z);
    m.fine = { vs: vs / f, map: fmap, f, region: R };
  }
  const coarseSkip = R ? isFine : null;
  for (const p of prims) {
    if (p.op) {
      if (fine && fine.paint === false) continue;
      if (fl) { try { p.op(fl); } catch (e) { /* a broken paint op must never break a unit */ } }
      if (!fl || p.coarseToo) { try { p.op(coarse); } catch (e) { /* ignore */ } }
      continue;
    }
    fillPrim(m.map, p, vs, null, coarseSkip);
    if (fl) fillPrim(fl.map, p, fl.vs, flim, fl.skip);
  }
  return m;
}

/* ---------------- meshing both layers ---------------- */
// A Map whose has() also reports cells that are solid in the other layer, so faces between the layers are culled.
class LayerMap extends Map {
  constructor(src, extra) { super(src); this.extra = extra; }
  has(k) { return super.has(k) || this.extra(k); }
}
function mergeGeos(a, b) {
  const g = new THREE.BufferGeometry();
  for (const name of ['position', 'normal', 'color', 'aPart', 'aEmit']) {
    const A = a.getAttribute(name), B = b.getAttribute(name);
    const arr = new Float32Array(A.array.length + B.array.length);
    arr.set(A.array, 0); arr.set(B.array, A.array.length);
    g.setAttribute(name, new THREE.Float32BufferAttribute(arr, A.itemSize));
  }
  const na = a.getAttribute('position').count, ia = a.index ? Array.from(a.index.array) : [], ib = b.index ? Array.from(b.index.array) : [];
  const idx = ia.concat(ib.map(i => i + na));
  g.setIndex(idx);
  g.computeBoundingSphere();
  a.dispose(); b.dispose();
  return g;
}
/* meshFn = meshAO (passed in to avoid an import cycle). ctr is in coarse cells. */
export function meshWithFine(m, team, ctr, meshFn, args) {
  const F = m.fine, f = F.f, R = F.region;
  const full = new Set(), cnt = new Map(), f3 = f * f * f;
  for (const k of F.map.keys()) {
    const [x, y, z] = DEC(k), ck = KEY(Math.floor(x / f), Math.floor(y / f), Math.floor(z / f));
    const n = (cnt.get(ck) || 0) + 1; cnt.set(ck, n); if (n === f3) full.add(ck);
  }
  const coarseMap = new LayerMap(m.map, k => full.has(k));
  // fine cells next to the region see coarse voxels as solid (coarse voxels inside the region are rare: weapon art)
  const fineMap = new LayerMap(F.map, k => { const [x, y, z] = DEC(k); return m.map.has(KEY(Math.floor(x / f), Math.floor(y / f), Math.floor(z / f))); });
  const ga = meshFn({ vs: m.vs, map: coarseMap }, team, ctr, ...args);
  const gb = meshFn({ vs: F.vs, map: fineMap }, team, { ...ctr, cx: ctr.cx * f, cz: ctr.cz * f, y0: ctr.y0 * f }, ...args);
  return mergeGeos(ga, gb);
}

/* Bounds of the fine layer in coarse cells (for modelCenter). */
export function fineBounds(m) {
  const F = m.fine; if (!F || !F.map.size) return null;
  let mny = Infinity, mxy = -Infinity;
  for (const k of F.map.keys()) { const y = ((k >> 10) & 1023) - 512; if (y < mny) mny = y; if (y > mxy) mxy = y; }
  return { y0: mny / F.f, y1: (mxy + 1) / F.f };
}

/* Total voxel count of a model including the fine layer. */
export const voxelCount = m => m.map.size + (m.fine ? m.fine.map.size : 0);

/* ---------------- head parts for the compilers ---------------- */
// signed distance (metres) to the head box with slightly rounded edges
function sdHead(px, py, pz, h) {
  const r = Math.min(h[0], h[1], h[2]) * 0.18;
  const qx = Math.abs(px) - h[0] + r, qy = Math.abs(py) - h[1] + r, qz = Math.abs(pz) - h[2] + r;
  return Math.hypot(Math.max(qx, 0), Math.max(qy, 0), Math.max(qz, 0)) + Math.min(Math.max(qx, qy, qz), 0) - r;
}
// volume op in head units: fn(u, v, w, d, current voxel) -> colour | null
function volumeOp(fr, part, B, fn) {
  return L => {
    const vs = L.vs;
    const lo = B[0].map((v, a) => Math.floor((fr.c[a] + v * fr.h[a]) / vs)), hi = B[1].map((v, a) => Math.floor((fr.c[a] + v * fr.h[a]) / vs));
    for (let z = lo[2]; z <= hi[2]; z++) for (let y = Math.max(0, lo[1]); y <= hi[1]; y++) for (let x = lo[0]; x <= hi[0]; x++) {
      const px = (x + 0.5) * vs - fr.c[0], py = (y + 0.5) * vs - fr.c[1], pz = (z + 0.5) * vs - fr.c[2];
      const cur = L.get(x, y, z);
      const c = fn(px / fr.h[0], py / fr.h[1], pz / fr.h[2], sdHead(px, py, pz, fr.h), cur);
      if (c !== null && c !== undefined) L.set(x, y, z, c, part);
    }
  };
}
const boundsBox = (fr, B) => ({ c: [0, 1, 2].map(a => fr.c[a] + (B[0][a] + B[1][a]) / 2 * fr.h[a]), h: [0, 1, 2].map(a => (B[1][a] - B[0][a]) / 2 * fr.h[a]) });

/**
 * Face, hair and headgear primitives for a head. Returns { before, after, add }:
 *   before: pushed after the template's base primitives (ears, muzzle, hair, hat),
 *   add:    the recipe's add entries (legacy face boxes removed when the recipe has no `face`),
 *   after:  pushed after the add entries (face painter, horns, teeth, beards).
 * ctx: { kind, head: {c,h}, jaw?: {c,h}, headPart, jawPart, skin? }; rPrim(frame, entry, part, mirror) from voxel.js.
 * Never throws: on any error the head simply stays plain.
 */
export function headPrims(rec, ctx0, rPrim) {
  if (faceMode() === 'aus') return { before: [], after: [], add: rec.add || [] }; // comparison switch (faceMode.js)
  let ctx = ctx0;
  const before = [], after = [];
  let add = rec.add || [];
  try {
    const lf = legacyFaceFilter(rec);
    add = lf.add;
    const r2 = lf.face ? { ...rec, face: lf.face } : rec;
    // robot heads modelled as a box around the whole head: paint the robot face onto that box
    const coverColors = [];
    if (rec.face && rec.face.type === 'roboter') for (const a of add) {
      const at = a.at || [0, 0, 0], sz = a.size || [1, 1, 1];
      if (a.on === 'head' && !a.rot && (a.shape || 'box') === 'box' && sz.every(v => v >= 0.95) && at.every(v => Math.abs(v) <= 0.25) && at[2] + sz[2] >= 1) {
        const c = typeof a.color === 'string' ? a.color : a.color && a.color.color;
        if (typeof c === 'string' && /^#[0-9a-f]{6}$/i.test(c)) coverColors.push(parseInt(c.slice(1), 16));
      }
    }
    ctx = { ...ctx, coverColors };
    const fp = faceParts(r2, ctx);
    const skin = fp.spec.skin;
    // free-form helmets/hats in `add` (wide entries reaching over the crown) cut the hair like library hats
    let hairCut = 9;
    const near = (a, b) => { const A = [16, 8, 0].map(k => (a >> k) & 255), B = [16, 8, 0].map(k => (b >> k) & 255); return Math.abs(A[0] - B[0]) + Math.abs(A[1] - B[1]) + Math.abs(A[2] - B[2]) < 90; };
    const hairHex = rec.hair && typeof rec.hair.color === 'string' && /^#[0-9a-f]{6}$/i.test(rec.hair.color) ? parseInt(rec.hair.color.slice(1), 16) : null;
    for (const a of add) {
      if (a.on !== 'head' || a.rot) continue;
      // boxes in hair or skin colour are hair/head shapes, not hats
      const ac = typeof a.color === 'string' ? a.color : a.color && a.color.color;
      const acHex = typeof ac === 'string' && /^#[0-9a-f]{6}$/i.test(ac) ? parseInt(ac.slice(1), 16) : null;
      if (acHex !== null && ((hairHex !== null && near(acHex, hairHex)) || near(acHex, skin))) continue;
      const at = a.at || [0, 0, 0], sz = a.size || [1, 1, 1];
      if (sz[0] >= 0.9 && sz[2] >= 0.85 && at[1] + sz[1] >= 0.98 && Math.abs(at[0]) < 0.2) hairCut = Math.min(hairCut, at[1] - sz[1]);
    }
    const hc = { ...ctx, skin, hairCut };
    const ent = (list, a, face) => {
      const part = a.on === 'jaw' && ctx.jawPart >= 0 ? ctx.jawPart : ctx.headPart;
      for (const mir of a.mirror ? [false, true] : [false]) { const p = rPrim(ctx.head, a, part, mir); p.hf = true; if (face) p.face = true; list.push(p); }
    };
    const op = (list, fn, B) => list.push({ op: fn, hf: !!B, ...(B ? boundsBox(ctx.head, B) : { c: ctx.head.c, h: ctx.head.h }) });
    fp.pre.forEach(a => ent(before, a, true));
    const hp = hairParts(r2, hc);
    if (hp) op(before, hp.op, hp.bounds);
    const ht = hatParts(r2, hc);
    if (ht) {
      ht.entries.forEach(a => ent(before, a, false));
      for (const o of ht.ops) op(before, volumeOp(ctx.head, ctx.headPart, o.bounds, o.fn), o.bounds);
    }
    if (fp.paint) op(after, fp.paint, null);
    fp.post.forEach(a => ent(after, a, false));
    for (const fn of fp.posts) op(after, fn, fp.opBounds);
  } catch (e) { /* keep the plain head */ }
  return { before, after, add };
}
